import { registerLocaleData } from '@angular/common'
import {
  APP_INITIALIZER,
  EnvironmentProviders,
  inject,
  Injectable,
  LOCALE_ID,
  makeEnvironmentProviders,
} from '@angular/core'
import { loadTranslations } from '@angular/localize'
import { RapService } from '../../modules/rap/rap.service'
import { SUPPORTED_LOCALES, I18nLocales, I18nTranslations } from './i18n.types'
import { getSupportedLocale, I18N_SUPPORTED_APP_IDS } from './i18n.utils'
import { select, Store } from '@ngrx/store'
import { AAAStore } from '../../store/root-reducer'
import { setDistanceUnit } from '../../modules/ui/ui.actions'
import { selectDistanceUnit } from '../../modules/ui/ui.selectors'
import { filter, map, take } from 'rxjs/operators'
import { AppWhiteLabelId } from '../utils/branding'

// Static imports for supported locales
import localeEn from '@angular/common/locales/en'
import localeFrCa from '@angular/common/locales/fr-CA'

@Injectable({
  providedIn: 'root',
})
class I18n {
  defaultLocale = SUPPORTED_LOCALES.EN_US
  localeByQueryParam = new URLSearchParams(window.location.search).get('locale')

  constructor(
    private store$: Store<AAAStore>,
    private rapService: RapService
  ) {}

  getNavigatorLocale = () =>
    navigator.languages.find((lang) => lang.indexOf('-') || lang.indexOf('_'))

  detectLocale() {
    const localeByPriority =
      this.localeByQueryParam ||
      localStorage.getItem('locale') ||
      this.getNavigatorLocale()

    const rapId = this.rapService.getRapAppId() as AppWhiteLabelId
    if (
      I18N_SUPPORTED_APP_IDS.includes(rapId) ||
      Boolean(this.localeByQueryParam) ||
      localStorage.getItem('locale')
    ) {
      return getSupportedLocale(localeByPriority)
    }
    return this.defaultLocale
  }

  async setLocale(i18nTranslations: I18nTranslations) {
    const locale = this.detectLocale()

    this.store$.pipe(
      select(selectDistanceUnit),
      take(1),
      filter((distanceUnit) => distanceUnit !== null),
      map(() => {
        const i18nLocale = I18nLocales().find((loc) => loc.locale === locale)
        this.store$.dispatch(
          setDistanceUnit({ payload: i18nLocale.defaultDistanceUnit })
        )
      })
    )

    // Register locale data based on detected locale
    switch (locale) {
      case SUPPORTED_LOCALES.FR_CA:
        registerLocaleData(localeFrCa)
        loadTranslations(i18nTranslations[SUPPORTED_LOCALES.FR_CA])
        break
      case SUPPORTED_LOCALES.EN_CA:
      case SUPPORTED_LOCALES.EN_US:
      default:
        registerLocaleData(localeEn)
        loadTranslations(i18nTranslations[SUPPORTED_LOCALES.EN_US])
        break
    }
  }
}

function setLocale(i18nTranslations: I18nTranslations) {
  return {
    provide: APP_INITIALIZER,
    useFactory: (i18n: I18n) => () => i18n.setLocale(i18nTranslations),
    deps: [I18n],
    multi: true,
  }
}

function setLocaleId() {
  return {
    provide: LOCALE_ID,
    useFactory: (i18n: I18n) => {
      const locale = i18n.detectLocale()
      localStorage.setItem('locale', locale)
      return locale
    },
    deps: [I18n],
  }
}

export function provideI18n(
  i18nTranslations: I18nTranslations
): EnvironmentProviders {
  return makeEnvironmentProviders([setLocale(i18nTranslations), setLocaleId()])
}
